import axios from 'axios'

import { API_BASE_URL, PUBLIC_API_BASE_URL } from './baseURL'
import { apiClient, apiRuntime, refreshSession, unwrap } from './runtime'
import type {
  AccountReferenceOptions,
  ApiEnvelope,
  AuthResponse,
  TerminalAgreement,
  EmployeeInvitationInspect,
} from './types'
import type { AltchaChallenge } from '@/features/auth/altcha'

const publicClient = axios.create({ baseURL: API_BASE_URL, withCredentials: true, timeout: 20_000 })
const publicSiteClient = axios.create({ baseURL: PUBLIC_API_BASE_URL, withCredentials: true, timeout: 20_000 })

function remember(auth: AuthResponse): AuthResponse {
  apiRuntime.setAccessToken(auth.accessToken)
  return auth
}

export const authApi = {
  captchaChallenge() {
    return publicClient.post<ApiEnvelope<AltchaChallenge>>('/h5/auth/captcha/challenge')
      .then((response) => unwrap(response.data))
  },
  async login(payload: { identifier: string; password: string; captchaPayload?: string }) {
    const response = await publicClient.post<ApiEnvelope<AuthResponse>>('/h5/auth/login', payload)
    return remember(unwrap(response.data))
  },
  async session() { return remember(await refreshSession()) },
  async logout() {
    await apiClient.post('/h5/auth/logout')
    apiRuntime.setAccessToken(null)
  },
  inspectInvitation(payload: { token?: string; email?: string; code?: string }) {
    return publicClient.post<ApiEnvelope<EmployeeInvitationInspect>>(
      '/h5/auth/employee-invitations/inspect', payload,
    ).then((response) => unwrap(response.data))
  },
  agreements() {
    return publicClient.get<ApiEnvelope<TerminalAgreement[]>>('/h5/legal/agreements')
      .then((response) => unwrap(response.data))
  },
  async acceptInvitation(payload: Record<string, unknown>) {
    const response = await publicClient.post<ApiEnvelope<AuthResponse>>(
      '/h5/auth/employee-invitations/accept', payload,
    )
    return remember(unwrap(response.data))
  },
  referenceOptions() {
    return publicSiteClient.get<ApiEnvelope<AccountReferenceOptions>>('/tenant-onboarding/options')
      .then((response) => unwrap(response.data))
  },
  forgotPassword(email: string) { return publicClient.post('/h5/auth/password/forgot', { email }) },
  resetPassword(token: string, password: string) {
    return publicClient.post('/h5/auth/password/reset', { token, password })
  },
  confirmEmail(token: string) { return publicClient.post('/h5/auth/email/confirm', { token }) },
  changeEmail(newEmail: string, currentPassword: string) {
    return apiClient.post('/h5/auth/email/change', { newEmail, currentPassword })
  },
}
